import Head from 'next/head';
import Image from 'next/image';
import Auction34GlobalStyles from '@/components/Auction34/GlobalStyles';
import MansionGlobalStyles from '@/components/Mansion/GlobalStyles';
import { useAzureLoginUrl } from '@/hooks/apiHooks';
import { A34_SIGN_SRC } from '@/components/Auction34/constants';
import { SrOnly } from '@/components/Auction34/styles';
import { LoginTone } from '@/components/Login/tone';
import {
  Action,
  AuctionCard,
  AuctionKicker,
  AuctionSign,
  AuctionStage,
  AuctionText,
  AuctionTitle,
  Content,
  ErrorBox,
  ErrorText,
  MansionFrame,
  MansionLabel,
  MansionRule,
  MansionStage,
  MansionText,
  MansionTitle,
  MsButton,
  MsButtonSkeleton,
  RetryButton,
  auctionCtaVars,
  mansionCtaVars,
} from '@/components/Login/styles';

/* ==========================================================================
   로그인 화면 (PORTAL-13709)

   들어온 경로에 따라 맨션상점(옥션) 톤과 34 MANSION 톤을 갈아 끼웁니다.
   판정은 tone.ts 가 `?next=` 로 하고, 이 컴포넌트는 결정된 톤만 받습니다.

   MS 로그인 버튼의 동작(로딩·실패·재시도)은 두 톤이 같아서 한 곳에 둡니다.
   ========================================================================== */

/** 버튼 안의 MS 로고. 20×20 원본이라 확대하지 않습니다. */
const MsMark = () => (
  <Image src={'/images/ms-icon.png'} width={20} height={20} alt={''} />
);

interface CtaProps {
  /** 톤별 색을 CSS 변수로 받습니다. */
  vars: Record<string, string>;
}

/**
 * MS 로그인 CTA.
 *
 * 예전에는 URL 을 못 받으면 스켈레톤이 영원히 돌았습니다. SWR 의 error 를 함께
 * 보고 실패를 화면에 알린 뒤 재시도(mutate)를 걸 수 있게 했습니다.
 */
const Cta = ({ vars }: CtaProps) => {
  const { data, error, isLoading, mutate } = useAzureLoginUrl();

  if (data?.msAzureLoginUrl) {
    return (
      <Action style={vars}>
        <MsButton href={data.msAzureLoginUrl}>
          <MsMark />
          {'Microsoft로 로그인'}
        </MsButton>
      </Action>
    );
  }

  // 아직 응답이 없고 에러도 없으면 로딩입니다.
  if (isLoading || (!error && !data)) {
    return (
      <Action style={vars}>
        {/* 자리만 잡는 표시라 스크린리더에는 상태 문구로 알립니다. */}
        <MsButtonSkeleton aria-hidden={'true'} />
        <SrOnly role={'status'}>{'로그인 준비 중'}</SrOnly>
      </Action>
    );
  }

  return (
    <Action style={vars}>
      <ErrorBox>
        <ErrorText role={'alert'}>
          {'로그인 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'}
        </ErrorText>
        <RetryButton type={'button'} onClick={() => mutate()}>
          {'다시 시도'}
        </RetryButton>
      </ErrorBox>
    </Action>
  );
};

/** 맨션상점(옥션) 톤 — 포털 루트로 들어온 경우입니다. */
const AuctionScene = () => (
  <>
    <Head>
      <link rel={'preconnect'} href={'https://fonts.googleapis.com'} />
      <link
        rel={'preconnect'}
        href={'https://fonts.gstatic.com'}
        crossOrigin={'anonymous'}
      />
      <link
        rel={'stylesheet'}
        href={
          'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable.min.css'
        }
      />
      <link
        rel={'stylesheet'}
        href={
          'https://fonts.googleapis.com/css2?family=Inter:wght@400..800&display=swap'
        }
      />
    </Head>

    <Auction34GlobalStyles />

    <AuctionStage>
      <Content>
        {/*
          문구는 아래 제목이 담당하므로 그림은 접근성 트리에서 뺍니다.
          원본 크기를 적어 두어야 로드 전에도 자리가 잡혀 카드가 밀리지 않습니다.
        */}
        <AuctionSign aria-hidden={'true'}>
          <img src={A34_SIGN_SRC} alt={''} width={624} height={1271} />
        </AuctionSign>

        <AuctionCard>
          <AuctionKicker>{'F&F 34TH ANNIVERSARY'}</AuctionKicker>
          {/* 간판이 그림이라 같은 문구를 제목으로 한 번 더 둡니다. */}
          <AuctionTitle>{'오늘만 열리는 맨션상점'}</AuctionTitle>
          <AuctionText>
            {'사내 Microsoft 계정으로 로그인하면 입장할 수 있습니다.'}
          </AuctionText>
          <Cta vars={auctionCtaVars} />
        </AuctionCard>
      </Content>
    </AuctionStage>
  </>
);

/** 34 MANSION 톤 — 하우스 안내(/mansion)로 들어온 경우입니다. */
const MansionScene = () => (
  <>
    <Head>
      <link rel={'preconnect'} href={'https://fonts.googleapis.com'} />
      <link
        rel={'preconnect'}
        href={'https://fonts.gstatic.com'}
        crossOrigin={'anonymous'}
      />
      <link
        rel={'stylesheet'}
        href={
          'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap'
        }
      />
      <link
        rel={'stylesheet'}
        href={
          'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/static/pretendard.min.css'
        }
      />
    </Head>

    {/*
      site 단계로 두어 밝은 배경(아이보리)을 씁니다. hero 단계의 남색은
      영상이 깔리는 화면용이라 로그인 화면에는 맞지 않습니다.
    */}
    <MansionGlobalStyles phase={'site'} />

    <MansionStage>
      <Content>
        <MansionFrame>
          <MansionLabel>{'F&F 34TH ANNIVERSARY'}</MansionLabel>
          <MansionTitle>{'34 MANSION'}</MansionTitle>
          <MansionRule />
          <MansionText className={'m-ko'}>
            {'당신의 하우스가 기다리고 있습니다.'}
            <br />
            {'사내 Microsoft 계정으로 로그인해 주세요.'}
          </MansionText>
          <Cta vars={mansionCtaVars} />
        </MansionFrame>
      </Content>
    </MansionStage>
  </>
);

interface Props {
  tone: LoginTone;
}

const Login = ({ tone }: Props) =>
  tone === 'mansion' ? <MansionScene /> : <AuctionScene />;

export default Login;
